import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { migratePostImages, scanExternalImages } from "@/lib/migrate-images.functions";

export const Route = createFileRoute("/admin/migrate-images")({
  head: () => ({
    meta: [
      { title: "Görsel Migrasyonu — Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: MigrateImagesPage,
});

type MigrateResult = Awaited<ReturnType<typeof migratePostImages>>;
type ScanResult = Awaited<ReturnType<typeof scanExternalImages>>;

function MigrateImagesPage() {
  const scan = useServerFn(scanExternalImages);
  const migrate = useServerFn(migratePostImages);
  const [busy, setBusy] = useState<"idle" | "scan" | "migrate">("idle");
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [result, setResult] = useState<MigrateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl mb-1">Görsel Migrasyonu</h1>
        <p className="text-sm text-ink/60">
          WordPress CDN (i0.wp.com / actnow4planetearth.com) üzerindeki tüm görselleri Lovable Cloud Storage'a (
          <code className="text-[11px] bg-ink/5 px-1.5 py-0.5 rounded">post-images</code>) indirir ve içerikteki URL'leri günceller.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <button
          disabled={busy !== "idle"}
          onClick={async () => {
            setBusy("scan"); setError(null); setResult(null);
            try { setScanResult(await scan()); }
            catch (e) { setError(e instanceof Error ? e.message : String(e)); }
            finally { setBusy("idle"); }
          }}
          className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded border border-ink/20 hover:border-ink disabled:opacity-50"
        >
          {busy === "scan" ? "Taranıyor…" : "1. Tara (dry-run)"}
        </button>
        <button
          disabled={busy !== "idle"}
          onClick={async () => {
            if (!confirm("Tüm dış görseller indirilip Lovable Cloud Storage'a yüklenecek ve yazılardaki URL'ler kalıcı olarak değiştirilecek. Devam edilsin mi?")) return;
            setBusy("migrate"); setError(null);
            try { setResult(await migrate()); }
            catch (e) { setError(e instanceof Error ? e.message : String(e)); }
            finally { setBusy("idle"); }
          }}
          className="px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded bg-clay text-paper disabled:opacity-50"
        >
          {busy === "migrate" ? "Taşınıyor…" : "2. Migrasyonu Başlat"}
        </button>
      </div>

      {error && (
        <div className="text-sm text-clay bg-clay/10 border border-clay/30 rounded p-3">{error}</div>
      )}

      {scanResult && (
        <section className="border border-ink/10 rounded p-4">
          <h2 className="font-serif text-lg mb-2">Tarama sonucu</h2>
          <ul className="text-sm space-y-1">
            <li>Toplam yazı: <strong>{scanResult.posts}</strong></li>
            <li>Dış görsel barındıran yazı: <strong>{scanResult.postsWithExternal}</strong></li>
            <li>Benzersiz görsel sayısı: <strong>{scanResult.uniqueImages}</strong></li>
          </ul>
          {scanResult.perPost.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs uppercase tracking-wider cursor-pointer text-ink/60">Detay</summary>
              <ul className="text-xs mt-2 space-y-1 font-mono">
                {scanResult.perPost.map((p) => (
                  <li key={p.slug}>{p.slug} — {p.count} görsel</li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}

      {result && (
        <section className="border border-ink/10 rounded p-4">
          <h2 className="font-serif text-lg mb-2">Migrasyon sonucu</h2>
          <ul className="text-sm space-y-1">
            <li>Taranan yazı: <strong>{result.scanned}</strong></li>
            <li>Güncellenen yazı: <strong>{result.rewritten}</strong></li>
            <li>Yüklenen görsel: <strong>{result.uploaded}</strong></li>
            <li>Hatalı: <strong className={result.failed.length > 0 ? "text-clay" : ""}>{result.failed.length}</strong></li>
          </ul>
          {result.changedPosts.length > 0 && (
            <details className="mt-3" open>
              <summary className="text-xs uppercase tracking-wider cursor-pointer text-ink/60">Güncellenen yazılar</summary>
              <ul className="text-xs mt-2 space-y-1 font-mono">
                {result.changedPosts.map((p) => (
                  <li key={p.slug}>{p.slug} — {p.replacements} URL değiştirildi</li>
                ))}
              </ul>
            </details>
          )}
          {result.failed.length > 0 && (
            <details className="mt-3" open>
              <summary className="text-xs uppercase tracking-wider cursor-pointer text-clay">Hatalar</summary>
              <ul className="text-xs mt-2 space-y-1 font-mono">
                {result.failed.map((f, i) => (
                  <li key={i} className="break-all">{f.url}: {f.error}</li>
                ))}
              </ul>
            </details>
          )}
        </section>
      )}
    </div>
  );
}
